import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import moment from "moment-timezone";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import {
  Grid,
  Drawer,
  Divider,
} from "@material-ui/core";
import IconButton from "@material-ui/core/IconButton";
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import DashboardWrapper from "../containers/DashboardWrapper";
import { setCurrentPage, getImageDataByFileName, getSavedSelectedTagDataByFileName, setPeriodType, setDrawerTagTip, getSavedCommentDataByFileName,
  getSavedSelectedTagDataByFileNameNtt, getSavedCommentDataByFileNameHokuden, setSelectedMissionLabel,
 } from "../actions";
import ImageMeta from "../components/Viewer/ImageMeta";
import ImageMagnify from "../components/Viewer/ImageMagnify";
import TagConfig from "../config/TagConfig";
import LocalStorageHelper from "../utils/helper/localstorage_helper";

const drawerWidth = 300;

const useStyles = makeStyles((theme) => ({
  contentGrid: {
    marginTop: theme.spacing(0),
    display: 'flex',
    justifyContent: 'center',  // 中央揃え
    alignItems: 'center',
  },
  imageMeta: {
    marginTop: 0,
    marginBottom: 8,
    maxWidth: 1200,
    width: '100%',           // 横幅を100%に
    marginLeft: 'auto',      // 左右中央に配置
    marginRight: 'auto',
  },
  centeredContainer: {
    display: 'flex',
    justifyContent: 'center', // 中央揃え
    alignItems: 'center',
    width: '100%',
    marginLeft: 'auto',
    marginRight: 'auto',
  },
  drawer: {
    width: drawerWidth,
    flexShrink: 0,
  },
  drawerPaper: {
    width: drawerWidth,
    top: 62,
  },
  drawerHeader: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0, 1),
    ...theme.mixins.toolbar,
    justifyContent: 'flex-start',
  },
  tagTextWrapper: {
    padding: "16px",
  },
  tagTip: {
    fontWeight: "bold",
    marginBottom: 16,
  },
  tagText: {
    fontSize: "14px",
    marginBottom: 16,
  }
}));

const Viewer = ({
  setCurrentPage,
  getImageDataByFileName,
  filterDrawerState,
  viewer,
  viewerImageData,
  getSavedSelectedTagDataByFileName,
  getSavedCommentDataByFileName,
  getSavedSelectedTagDataByFileNameNtt,
  getSavedCommentDataByFileNameHokuden,
  setPeriodType,
  setDrawerTagTip,
  periodType,
  openTagTip,
  setSelectedMissionLabel,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const [tagData, setTagData] = useState([]);
  const companyName = LocalStorageHelper.getCompany();

  useEffect(() => {
    setCurrentPage(window.location.pathname);

    // --- クエリパラメータから mission を取得してセット ---
    const url = new URL(window.location.href);
    const missionFromQuery = url.searchParams.get("mission");
    if (missionFromQuery) {
      // URLSearchParams で既に decode 済み
      setSelectedMissionLabel(missionFromQuery);
      setPeriodType("mission");
    } else {
      setPeriodType("season");
    }

    // URLパスからファイル名を取得
    const pathParts = window.location.pathname.split("/");
    const encodedFileName = pathParts[pathParts.length - 1];
    const fileName = decodeURIComponent(encodedFileName);

    if (fileName) {
      getImageDataByFileName(fileName);
      if (companyName === "tepco") {
        getSavedCommentDataByFileName(fileName);
      } else if (companyName === "hokuden") {
        getSavedCommentDataByFileNameHokuden(`${fileName}.jpg`);
      } else if (companyName === "ntt" || companyName === "ntt-ft" || companyName === "okiden") {
        getSavedSelectedTagDataByFileName(`${fileName}.jpg`);
        getSavedCommentDataByFileName(`${fileName}.jpg`);
      }
    }
    setTagData(TagConfig.seasonTag);
  }, []);

  useEffect(() => {
    if (viewerImageData.length > 0 && viewerImageData[0] !== undefined) {
      if (viewerImageData[0]["type"] === "SEASON") {
        setTagData(TagConfig.seasonTag);
        setPeriodType("season");
      } else {
        setTagData(TagConfig.missionTagB.concat(TagConfig.missionTagC));
        setPeriodType("mission");
      }
      if (companyName === "ntt" || companyName === "ntt-ft" || companyName === "okiden") {
        getSavedSelectedTagDataByFileNameNtt(`${viewerImageData[0]["review_request_id"]}`);
      }
    }
  }, [viewerImageData]);

  return (
    <DashboardWrapper>
      <Grid container spacing={0} direction="row" justifyContent="center">
        <Grid item xs={12}>
          <Grid
            container
            justifyContent={"center"}
            alignItems={"center"}
            className={classes.contentGrid}
          >
            <Grid item xs={12} md={12} lg={10} className={classes.centeredContainer} style={{ marginBottom: 24 }}>
              <ImageMeta viewer={viewerImageData} />
            </Grid>
            <Grid item xs={12} md={12} lg={10} className={classes.centeredContainer}>
              {viewerImageData && viewerImageData.length > 0 ? (
                <ImageMagnify viewer={viewerImageData} />
              ) : (
                null
              )}
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Drawer
        className={classes.drawer}
        variant="persistent"
        anchor="right"
        open={openTagTip}
        classes={{
          paper: classes.drawerPaper,
        }}
      >
        <div className={classes.drawerHeader}>
          <IconButton onClick={() => setDrawerTagTip(false)}>
            {theme.direction === 'rtl' ? <ChevronLeftIcon /> : <ChevronRightIcon />}
          </IconButton>
        </div>
        <Divider />
        <div className={classes.tagTextWrapper}>
          {tagData.length > 0 &&
            <>
              <div className={classes.tagTip}>
                {periodType === "season" ? "シーズン" : "ミッション"}結果タグ説明
              </div>
              {tagData.length > 0 && tagData.map((row, index) => (
                <div className={classes.tagText} key={index}>{row["tip"]}</div>
              ))}
            </>
          }
        </div>
      </Drawer>
    </DashboardWrapper>
  );
};

Viewer.propTypes = {
  classes: PropTypes.object,
};

const mapStateToProps = (state) => ({
  filterDrawerState: state.app.filterDrawerState,
  viewer: state.viewer.imageDataByFilename,
  viewerImageData: state.viewer.viewerImageData,
  periodType: state.app.periodType,
  openTagTip: state.app.openTagTip,
});
// signedUrl
const mapDispatchToProps = (dispatch) => ({
  setCurrentPage: (param) => dispatch(setCurrentPage(param)),
  getImageDataByFileName: (param) => dispatch(getImageDataByFileName(param)),
  getSavedSelectedTagDataByFileName: (param) => dispatch(getSavedSelectedTagDataByFileName(param)),
  getSavedCommentDataByFileName: (param) => dispatch(getSavedCommentDataByFileName(param)),
  setPeriodType: (param) => dispatch(setPeriodType(param)),
  setDrawerTagTip: (param) => dispatch(setDrawerTagTip(param)),
  getSavedSelectedTagDataByFileNameNtt: (param) => dispatch(getSavedSelectedTagDataByFileNameNtt(param)),
  getSavedCommentDataByFileNameHokuden: (param) => dispatch(getSavedCommentDataByFileNameHokuden(param)),
  setSelectedMissionLabel: (param) => dispatch(setSelectedMissionLabel(param)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Viewer);
